import { Button } from "@/components/ui/button"
import { toast } from '@/components/ui/use-toast'
import { Level, PharmacyAccess, PharmacyStatus } from '@/gen/auth/common_pb'
import { GetPharmacyInfoRequest } from '@/gen/auth/get_pharmacy_info_pb'
import { SwitchContextRequest } from '@/gen/auth/switch_context_pb'
import { getPharmacyInfo, switchPharmacyContext } from '@/gen/pharmacy-PharmacyService_connectquery'
import { useMutation } from '@connectrpc/connect-query'
import { Boxes, Building2, MapPin, X } from 'lucide-react'
import { useEffect, useState } from 'react'


const SelectAccess = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [selectedLevel, setSelectedLevel] = useState<Level | null>(null)
  const [accessList, setAccessList] = useState<PharmacyAccess[]>([])

  useEffect(() => {
    const storedAccess = localStorage.getItem('userAccessList')
    if (!storedAccess) {
      toast({
        title: 'Session Expired',
        description: 'Please login again',
        variant: 'destructive'
      })
      window.location.href = '/login'
      return
    }
    
    try {
      const parsed = JSON.parse(storedAccess)
      console.log('Loaded access list:', parsed) // Debug log
      
      // Handle both fresh login data and stored data
      if (Array.isArray(parsed) && parsed.length > 0) {
        setAccessList(parsed)
      } else {
        console.warn('Access list is empty or invalid')
        toast({
          title: 'No Access Data',
          description: 'Please login again to refresh your access list',
          variant: 'destructive'
        })
        // Don't redirect immediately, show the message
      }
    } catch (error) {
      console.error('Failed to parse access list:', error)
      window.location.href = '/login'
    }
  }, [])

  const getPharmacyInfoMutation = useMutation(getPharmacyInfo, {
    onSuccess: (response) => {
      if (response.status !== PharmacyStatus.STATUS_ACTIVE) {
        toast({
          title: 'Error',
          description: 'Pharmacy is not active',
          variant: 'destructive',
          duration: 5000
        })
        setIsLoading(false)
        return
      }
      
      // Update localStorage with new pharmacy info
      localStorage.setItem('level', selectedLevel?.toString() || '')
      localStorage.setItem('pharmacyName', response.pharmacyName || '')
      localStorage.setItem('companyName', response.companyName || '')
      localStorage.setItem('companyCode', response.companyCode || '')
      localStorage.setItem('pharmacyCode', response.pharmacyCode || '')
      localStorage.setItem('stockPointId', response.stockPointId || '')
      localStorage.setItem('stockPointName', response.stockPointName || '')
      localStorage.setItem('pharmacyMetadata', JSON.stringify(response.metadata) || '')
      localStorage.setItem('stockPointType', response.stockPointType.toString() || '')
      localStorage.setItem('pharmacySettings', JSON.stringify(response.settings) || '')
      localStorage.setItem('pharmacyLocationDetails', JSON.stringify(response.locationDetails) || '')
      localStorage.setItem('pharmacySetupDetails', JSON.stringify(response.setupDetails) || '')
      localStorage.setItem('pharmacySize', response.size.toString() || '')
      
      // Navigate based on level
      if (selectedLevel === Level.COMPANY) {
        window.location.href = '/company/dashboard'
      } else if (selectedLevel === Level.BRANCH) {
        window.location.href = '/branch/dashboard'
      } else if (selectedLevel === Level.STOCK_POINT) {
        window.location.href = '/overview'
      }
    },
    onError: (error) => {
      console.error("Pharmacy info error:", error)
      toast({
        title: 'Error',
        description: error?.message || 'Failed to fetch pharmacy info',
        variant: 'destructive',
        duration: 5000
      })
      setIsLoading(false)
    }
  })

  const switchContextMutation = useMutation(switchPharmacyContext, {
    onSuccess: (response) => {
      // Update tokens in localStorage
      localStorage.setItem('token', response.token)
      localStorage.setItem('refreshToken', response.refreshToken)
      localStorage.setItem('tokenExpiresAt', response.expiresAt.toString())
      localStorage.setItem('roles', JSON.stringify(response.roles))
      localStorage.setItem('firstName', response.userFirstName || '')
      localStorage.setItem('lastName', response.userLastName || '')
      
      // Fetch pharmacy info
      const pharmacyInfoRequest = new GetPharmacyInfoRequest()
      if (selectedId && selectedLevel) {
        if (selectedLevel === Level.COMPANY) {
          pharmacyInfoRequest.companyCode = selectedId
        } else if (selectedLevel === Level.BRANCH) {
          pharmacyInfoRequest.pharmacyCode = selectedId
        } else if (selectedLevel === Level.STOCK_POINT) {
          pharmacyInfoRequest.stockPointId = selectedId
        }
      }
      getPharmacyInfoMutation.mutate(pharmacyInfoRequest)
    },
    onError: (error) => {
      console.error("Switch context error:", error)
      toast({
        title: 'Error',
        description: error?.message || 'Failed to switch context',
        variant: 'destructive',
        duration: 5000
      })
      setIsLoading(false)
    }
  })

  const handleSelectAccess = (id: string, level: Level) => {
    setIsLoading(true)
    setSelectedId(id)
    setSelectedLevel(level)
    
    const request = new SwitchContextRequest()
    request.id = id
    request.level = level
    
    switchContextMutation.mutate(request)
  }

  return (
    <div className="relative min-h-screen w-full bg-white p-8">
      <div className="flex flex-col h-full">
        {/* Header with Back Button */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => window.history.back()}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            disabled={isLoading}
          >
          <div className="relative inline-flex items-center justify-center w-10 h-10 rounded-sm border-2 border-current">
            <X className="w-5 h-5" />
          </div>
          </button>
        </div>

        {/* Title */}
        <div className="flex flex-col items-center justify-center mb-8">
          <div className="flex flex-row items-center gap-4">
            <div className="w-12 h-12 rounded-md bg-blue-600 flex items-center justify-center">
              <img src="/logo.svg" alt="Logo" className="w-8 h-8" />
            </div>
            <h1 className="text-2xl font-semibold text-black">Select Pharmacy Access</h1>
          </div>
          <p className="text-gray-500 text-center text-base max-w-3xl mt-2">
            Configure your pharmacy access by selecting your company, branch or stock point
          </p>
        </div>

        {/* Access Grid */}
        <div className="flex flex-col gap-6 flex-1 overflow-auto pb-4">
          {/* Company Section */}
          <div className="flex flex-col bg-white shadow-xl rounded-lg p-6 min-h-[200px] max-h-[280px]">
            <div className="flex flex-row items-center gap-2 mb-1 px-2">
              <Building2 className="w-6 h-6 text-black" />
              <h2 className="text-xl font-semibold text-black">Company</h2>
            </div>
            <p className="text-gray-500 text-sm mb-4 px-2">Select your organisation</p>
            <div className="overflow-y-auto min-h-0 flex-1 pr-2">
              <div className="grid grid-cols-4 gap-4 content-start">
              {accessList.length > 0 ? (
                accessList
                .filter(access => access.level === Level.COMPANY)
                  .map((access) => (
                      <div
                        key={access.id}
                        className={`flex items-center justify-between bg-white p-3 rounded-md border-[0.5px] cursor-pointer border-gray-300 hover:border-blue-600 transition-colors`}
                        onClick={() => !isLoading && handleSelectAccess(access.id, access.level)}
                      >
                        <span className="text-sm text-black font-medium truncate mr-3">
                          {access.name}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          className="shrink-0"
                        >
                          {'Select'}
                        </Button>
                      </div>
                    ))
                ) : (
                  <p className="text-gray-400 text-sm col-span-4">No company access available</p>
                )}
              </div>
            </div>
          </div>

          {/* Branch Section */}
          <div className="flex flex-col bg-white shadow-xl rounded-lg p-6 min-h-[200px] max-h-[280px]">
            <div className="flex flex-row items-center gap-2 mb-1 px-2">
              <MapPin className="w-6 h-6 text-black" />
              <h2 className="text-xl font-semibold text-black">Branch</h2>
            </div>
            <p className="text-gray-500 text-sm mb-4 px-2">Choose your pharmacy branch</p>
            <div className="overflow-y-auto min-h-0 flex-1 pr-2">
              <div className="grid grid-cols-4 gap-4 content-start">
                {accessList.length > 0 ? (
                  accessList
                  .filter(access => access.level === Level.BRANCH)
                    .map((access) => (
                      <div
                        key={access.id}
                        className={`flex items-center justify-between bg-white p-3 rounded-md border-[0.5px] cursor-pointer border-gray-300 hover:border-blue-600 transition-colors`}
                        onClick={() => !isLoading && handleSelectAccess(access.id, access.level)}
                      >
                        <span className="text-sm text-black font-medium truncate mr-3">
                          {access.name}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          className="shrink-0"
                        >
                          {'Select'}
                        </Button>
                      </div>
                    ))
                ) : (
                  <p className="text-gray-400 text-sm col-span-4">No branch access available</p>
                )}
              </div>
            </div>
          </div>

          {/* Stock Point Section */}
          <div className="flex flex-col bg-white shadow-xl rounded-lg p-6 min-h-[200px] max-h-[280px]">
            <div className="flex flex-row items-center gap-2 mb-1 px-2">
              <Boxes className="w-6 h-6 text-black" />
              <h2 className="text-xl font-semibold text-black">Stock Point</h2>
            </div>
            <p className="text-gray-500 text-sm mb-4 px-2">
              Select stock points for inventory management
            </p>
            <div className="overflow-y-auto min-h-0 flex-1 pr-2">
              <div className="grid grid-cols-4 gap-4 content-start">
                {accessList.length > 0 ? (
                  accessList
                  .filter(access => access.level === Level.STOCK_POINT)
                    .map((access) => (
                      <div
                        key={access.id}
                        className={`flex items-center justify-between bg-white p-3 rounded-md border-[0.5px] cursor-pointer border-gray-300 hover:border-blue-600 transition-colors`}
                        onClick={() => !isLoading && handleSelectAccess(access.id, access.level)}
                      >
                        <span className="text-sm text-black font-medium truncate mr-3">
                          {access.name}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          className="shrink-0"
                        >
                          {'Select'}
                        </Button>
                      </div>
                    ))
                ) : (
                  <p className="text-gray-400 text-sm col-span-4">No stock point access available</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SelectAccess